import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/home/circleci/repo/node_modules/gatsby-theme-docz/src/base/Layout.js";
import { Playground, Props } from 'docz';
import { Currency, CurrencyProvider, useCurrency, withCurrency } from './currency';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "currency"
    }}>{`Currency`}</h1>
    <p>{`Similar to numbers, how currency is formatted depends on the locale. Stele uses
the browser's
`}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat"
      }}><inlineCode parentName="a">{`Intl.NumberFormat`}</inlineCode></a>{`
API to ensure formatting matches what users expect to see. The `}<inlineCode parentName="p">{`<Currency>`}</inlineCode>{`
component accepts as props any options you would otherwise pass to
`}<inlineCode parentName="p">{`Intl.Numberformat`}</inlineCode>{` as well as some more convenient formatting props
(`}<a parentName="p" {...{
        "href": "#props"
      }}>{`see below`}</a>{`).`}</p>
    <h2 {...{
      "id": "basic-usage"
    }}>{`Basic Usage`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import * as React from 'react'
import { CurrencyProvider, Currency } from '@patreon/stele'

function MyComponent(props) {
    const { myPrice } = props
    return (
        // wrap a top level component with a <CurrencyProvider> to provide
        // that currency via context to descendent components
        <CurrencyProvider value="USD">
            <Text intl>
                <p>
                    The price in the US is <Currency value={myPrice} />
                </p>
                <p>
                    {/* override currency directly via a prop */}
                    In the UK it will cost you <Currency
                        currency="GBP"
                        value={myPrice}
                    />
                </p>
                <p>
                    {/* set a new currency context with another provider */}
                    <CurrencyProvider value="EUR">
                        {/* override format for a specific locale */}
                        The price in the EU is <Currency
                            value={myPrice}
                            locale="de-DE"
                        />
                    </CurrencyProvider>
                </p>
            </Text>
        </CurrencyProvider>
    )
}
`}</code></pre>
    <h2 {...{
      "id": "example-message"
    }}>{`Example message`}</h2>
    <pre><code parentName="pre" {...{}}>{`The price in the US is { myPrice, number, currency }
`}</code></pre>
    <h2 {...{
      "id": "props"
    }}>{`Props`}</h2>
    <Props of={Currency} mdxType="Props" />
    <h2 {...{
      "id": "using-currency-context"
    }}>{`Using Currency context`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import * as React from 'react'
import { CurrencyProvider, useCurrency } from '@patreon/stele'

function ParentComponent(props) {
    return (
        <CurrencyProvider value="USD">
            <ConsumingComponent />
            <WrappedComponent />
        </CurrencyProvider>
    )
}

// access the currently set currency from context with a hook
function ConsumingComponent(props) {
    const currency = useCurrency()
    return (
        <div>
            currency from context hook: {currency} {/* USD */}
        </div>
    )
}

// or access it via a higher order component
const WrappedComponent = withCurrency(function InnerComponent(props) {
    const { currency } = props
    return (
        <div>
            currency from prop: {currency} {/* USD */}
        </div>
    )
})
`}</code></pre>
    <h2 {...{
      "id": "playground"
    }}>{`Playground`}</h2>
    <Playground __position={1} __code={'() => {\n  const [currency, setCurrency] = React.useState(\'USD\')\n  const [alwaysShowDollarName, setAlwaysShowDollarName] = React.useState(\n    false,\n  )\n  const [locale, setLocale] = React.useState(\'en-US\')\n  const [price, setPrice] = React.useState(5)\n  const [truncateDecimal, setTruncateDecimal] = React.useState(false)\n  return (\n    <>\n      {/* usage */}\n      <CurrencyProvider value={currency}>\n        <h3>\n          <Currency\n            alwaysShowDollarName={alwaysShowDollarName}\n            locale={locale}\n            truncateDecimal={truncateDecimal}\n            value={price}\n          />\n        </h3>\n      </CurrencyProvider>\n      {/* controls */}\n      <div>\n        <label htmlFor=\"price\">Price</label>\n        <input\n          name=\"price\"\n          value={price}\n          onChange={e => setPrice(e.target.value)}\n          type=\"number\"\n        />\n      </div>\n      <div>\n        <label htmlFor=\"currency\">Currency</label>\n        <select\n          name=\"currency\"\n          value={currency}\n          onChange={e => setCurrency(e.target.value)}\n        >\n          <option value=\"CAD\">Canadian Dollars</option>\n          <option value=\"USD\">US Dollars</option>\n          <option value=\"GBP\">UK Pounds</option>\n          <option value=\"EUR\">Euros</option>\n        </select>\n      </div>\n      <div>\n        <label htmlFor=\"locale\">Locale</label>\n        <select\n          name=\"locale\"\n          value={locale}\n          onChange={e => setLocale(e.target.value)}\n        >\n          <option value=\"en-US\">en-US</option>\n          <option value=\"en-CA\">en-CA</option>\n          <option value=\"de-DE\">de-DE</option>\n        </select>\n      </div>\n      <div>\n        <label htmlFor=\"truncate\">Truncate Decimal?</label>\n        <input\n          name=\"truncate\"\n          value={truncateDecimal}\n          onChange={e => setTruncateDecimal(!truncateDecimal)}\n          type=\"checkbox\"\n        />\n      </div>\n      <div>\n        <label htmlFor=\"always-show-dollar-name\">\n          Always Show Dollar Name?\n        </label>\n        <input\n          name=\"always-show-dollar-name\"\n          value={alwaysShowDollarName}\n          onChange={e => setAlwaysShowDollarName(!alwaysShowDollarName)}\n          type=\"checkbox\"\n        />\n      </div>\n    </>\n  )\n}'} __scope={{
      props,
      DefaultLayout,
      Playground,
      Props,
      Currency,
      CurrencyProvider,
      useCurrency,
      withCurrency
    }} mdxType="Playground">
    {() => {
        const [currency, setCurrency] = React.useState('USD');
        const [alwaysShowDollarName, setAlwaysShowDollarName] = React.useState(false);
        const [locale, setLocale] = React.useState('en-US');
        const [price, setPrice] = React.useState(5);
        const [truncateDecimal, setTruncateDecimal] = React.useState(false);
        return <>
                {
            /* usage */
          }
                <CurrencyProvider value={currency} mdxType="CurrencyProvider">
                    <h3>
                        <Currency alwaysShowDollarName={alwaysShowDollarName} locale={locale} truncateDecimal={truncateDecimal} value={price} mdxType="Currency" />
                    </h3>
                </CurrencyProvider>
                {
            /* controls */
          }
                <div>
                    <label htmlFor="price">Price</label>
                    <input name="price" value={price} onChange={e => setPrice(e.target.value)} type="number" />
                </div>
                <div>
                    <label htmlFor="currency">Currency</label>
                    <select name="currency" value={currency} onChange={e => setCurrency(e.target.value)}>
                        <option value="CAD">Canadian Dollars</option>
                        <option value="USD">US Dollars</option>
                        <option value="GBP">UK Pounds</option>
                        <option value="EUR">Euros</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="locale">Locale</label>
                    <select name="locale" value={locale} onChange={e => setLocale(e.target.value)}>
                        <option value="en-US">en-US</option>
                        <option value="en-CA">en-CA</option>
                        <option value="de-DE">de-DE</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="truncate">Truncate Decimal?</label>
                    <input name="truncate" value={truncateDecimal} onChange={e => setTruncateDecimal(!truncateDecimal)} type="checkbox" />
                </div>
                <div>
                    <label htmlFor="always-show-dollar-name">
                        Always Show Dollar Name?
                    </label>
                    <input name="always-show-dollar-name" value={alwaysShowDollarName} onChange={e => setAlwaysShowDollarName(!alwaysShowDollarName)} type="checkbox" />
                </div>
            </>;
      }}
    </Playground>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      